import {DownloadOutlined} from "@ant-design/icons";
import {Button, Input, message, Modal, Progress, Select, TreeSelect, Typography} from "antd";
import React, {useCallback, useMemo, useState} from "react";
import axios from "axios";
import {getId, isSuccessfulUploadStatus} from "../../helpers/file";
import {addFailedFile} from "../../helpers/uploadState";
import {downloadToCsv} from "../../helpers/csv";
import eventBus from '../../utils/eventBus';
import { useTranslation } from '../../hooks/useTranslation';
import {buildDirTree, mapDirTreeTitles} from '../../utils/dirTree';

const { TextArea } = Input;
const { Text } = Typography;
const { Option } = Select;

// 导出 eventBus 以保持向后兼容性
export { eventBus };

type UploadStatus = 'pending' | 'uploading' | 'success' | 'error';

interface UploadProgressItem {
  sourceUrl: string;
  fileName: string;
  status: UploadStatus;
  progress: number;
  targetUrl?: string;
  errorMessage?: string;
  message?: string;
}

interface UploadResult {
  totalCount: number;
  successCount: number;
  errorCount: number;
  items: UploadProgressItem[];
  completedAt: Date | null;
}

interface UploadApiResponse {
  url?: string;
}

type FetchMode = 'fast' | 'ai';

interface LoginInfoResponse {
  isSuccess?: boolean;
  enableAiUrlFetch?: boolean;
  detail?: {
    enableAiUrlFetch?: boolean;
    config?: string | Record<string, unknown>;
  };
}

const createEmptyUploadResult = (): UploadResult => ({
  totalCount: 0,
  successCount: 0,
  errorCount: 0,
  items: [],
  completedAt: null
});

const buildCsvFileName = () => {
  const now = new Date();
  const pad = (value: number) => `${value}`.padStart(2, '0');

  return `url-upload-result-${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}-${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
};

const UrlUpload = () => {
  const { t } = useTranslation();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [urls, setUrls] = useState("");
  const [selectedDir, setSelectedDir] = useState("url_upload");
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<UploadProgressItem[]>([]);
  const [randomPath, setRandomPath] = useState(false);
  const [fetchMode, setFetchMode] = useState<FetchMode>('fast');
  const [enableAiUrlFetch, setEnableAiUrlFetch] = useState(false);
  const [isLoadingAiPermission, setIsLoadingAiPermission] = useState(false);
  
  const [uploadResult, setUploadResult] = useState<UploadResult>(createEmptyUploadResult());
  
  // 目录列表（扁平），从接口动态获取
  const [dirList, setDirList] = useState<string[]>(['url_upload']);
  const [isLoadingDirs, setIsLoadingDirs] = useState(false);

  const formatDirLabel = useCallback((value: string) => {
    if (value === '__all__' || value === '全部目录') return t('common.allDirectories');
    if (value === '__default__' || value === '默认目录') return t('common.defaultDirectory');
    if (value === 'url_upload') return t('urlUpload.urlUploadDirectory');
    return value;
  }, [t]);

  const dirTreeData = useMemo(() => {
    const tree = buildDirTree(dirList);
    return mapDirTreeTitles(tree, formatDirLabel);
  }, [dirList, formatDirLabel]);

  const successItems = useMemo(
    () => uploadResult.items.filter((item) => item.status === 'success'),
    [uploadResult.items]
  );
  const errorItems = useMemo(
    () => uploadResult.items.filter((item) => item.status === 'error'),
    [uploadResult.items]
  );

  // 获取目录列表
  const fetchDirectoryList = async () => {
    try {
      setIsLoadingDirs(true);
      const response = await axios.get('/img-dir/list');

      console.info('获取目录列表成功:', response?.data);
      const data = response?.data?.data;
      if (response.data && Array.isArray(data)) {
        const dirs = data as string[];
        const list = dirs.length > 0 ? dirs : ['url_upload'];

        setDirList(list);

        if (!list.includes(selectedDir)) {
          setSelectedDir(list[0] || 'url_upload');
        }
      } else {
        console.error('获取目录列表失败:', response.data);
        const fallback = ['url_upload', 'images', 'temp'];
        setDirList(fallback);
        if (!fallback.includes(selectedDir)) setSelectedDir('url_upload');
      }
    } catch (error) {
      console.error('获取目录列表失败:', error);
      const fallback = ['url_upload', 'images', 'temp'];
      setDirList(fallback);
      if (!fallback.includes(selectedDir)) setSelectedDir('url_upload');
    } finally {
      setIsLoadingDirs(false);
    }
  };

  const fetchAiPermission = async () => {
    try {
      setIsLoadingAiPermission(true);
      const response = await axios.get<LoginInfoResponse>('/login/info');
      const data = response?.data;

      if (data?.enableAiUrlFetch === true || data?.detail?.enableAiUrlFetch === true) {
        setEnableAiUrlFetch(true);
        return;
      }

      if (typeof data?.detail?.config === 'string') {
        const config = JSON.parse(data.detail.config || '{}');
        setEnableAiUrlFetch(config.enableAiUrlFetch === true);
        return;
      }

      if (data?.detail?.config && typeof data.detail.config === 'object') {
        setEnableAiUrlFetch((data.detail.config as Record<string, unknown>).enableAiUrlFetch === true);
        return;
      }

      setEnableAiUrlFetch(false);
    } catch (error) {
      console.error('获取 AI 抓取权限失败:', error);
      setEnableAiUrlFetch(false);
    } finally {
      setIsLoadingAiPermission(false);
    }
  };

  const showModal = () => {
    setIsModalVisible(true);
    setUrls("");
    setUploadProgress([]);
    setRandomPath(false);
    setFetchMode('fast');
    setEnableAiUrlFetch(false);
    setUploadResult(createEmptyUploadResult());
    
    // 打开弹窗时获取目录列表
    fetchDirectoryList();
    fetchAiPermission();
  };

  const handleCancel = () => {
    if (!isUploading) {
      setIsModalVisible(false);
      setUrls("");
      setUploadProgress([]);
      setRandomPath(false);
      setFetchMode('fast');
      setEnableAiUrlFetch(false);
      setUploadResult(createEmptyUploadResult());
    } else {
      message.warning(t('urlUpload.stopUploadWarning'));
    }
  };

  const validateUrls = (urlText: string): string[] => {
    const lines = urlText.split('\n').map(line => line.trim()).filter(line => line.length > 0);
    
    if (lines.length === 0) {
      throw new Error(t('urlUpload.urlRequired'));
    }
    
    if (lines.length > 500) {
      throw new Error(t('urlUpload.urlLimitExceeded'));
    }

    const validUrls: string[] = [];
    const invalidUrls: string[] = [];

    lines.forEach((line, index) => {
      try {
        const url = new URL(line);
        if (url.protocol === 'http:' || url.protocol === 'https:') {
          validUrls.push(line);
        } else {
          invalidUrls.push(t('urlUpload.invalidUrlLine', { line: index + 1, url: line }));
        }
      } catch {
        invalidUrls.push(t('urlUpload.invalidUrlLine', { line: index + 1, url: line }));
      }
    });

    if (invalidUrls.length > 0) {
      throw new Error(t('urlUpload.invalidUrls', { urls: invalidUrls.join('\n') }));
    }

    return validUrls;
  };

  // 从URL中提取文件名，使用完整的path部分
  const extractFileNameFromUrl = (url: string): string => {
    try {
      const urlObj = new URL(url);
      const pathname = urlObj.pathname;
      
      // 如果pathname为空或者只有斜杠，使用域名作为文件名
      if (!pathname || pathname === '/') {
        return `${urlObj.hostname}_${Date.now()}`;
      }
      
      // 移除开头的斜杠，获取完整的path部分作为文件名
      let fileName = pathname.startsWith('/') ? pathname.substring(1) : pathname;
      
      // 如果没有文件扩展名，尝试从URL参数或查询字符串中获取
      if (!fileName.includes('.')) {
        // 检查是否有查询参数包含文件信息
        const searchParams = urlObj.searchParams;
        const fileParam = searchParams.get('file') || searchParams.get('name') || searchParams.get('filename');
        if (fileParam && fileParam.includes('.')) {
          fileName = fileParam;
        } else {
          // 如果还是没有扩展名，添加默认扩展名
          fileName = `${fileName}_${Date.now()}`;
        }
      }
      
      // 清理文件名中的特殊字符，但保留斜杠（用于路径分隔）
      fileName = fileName.replace(/[<>:"\\|?*]/g, '_');
      
      return fileName;
    } catch {
      // 如果URL解析失败，使用时间戳作为文件名
      return `url_upload_${Date.now()}`;
    }
  };

  const getUploadErrorMessage = (error: unknown) => {
    if (error instanceof Error) {
      return error.message;
    }
    if (typeof error === 'object' && error !== null) {
      const errorLike = error as {
        response?: { data?: { message?: string } };
        message?: string;
      };
      return errorLike.response?.data?.message || errorLike.message || t('urlUpload.uploadFailed');
    }
    return t('urlUpload.uploadFailed');
  };

  const updateUploadItem = (
    items: UploadProgressItem[],
    index: number,
    patch: Partial<UploadProgressItem>
  ) => {
    items[index] = {
      ...items[index],
      ...patch
    };
    setUploadProgress([...items]);
  };

  const uploadSingleUrl = async (
    url: string,
    fileName: string,
    onProgress: (progress: number) => void
  ): Promise<string> => {
    const formData = new FormData();
    formData.append('fromUrl', url);
    formData.append('fileName', fileName);
    formData.append('uid', getId() || '');
    formData.append('dir', selectedDir);
    formData.append('randomPath', randomPath.toString());
    formData.append('fetchMode', fetchMode);

    const response = await axios.post<UploadApiResponse>('/img/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      validateStatus: isSuccessfulUploadStatus,
      onUploadProgress: (progressEvent) => {
        if (progressEvent.total) {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(progress);
        }
      }
    });

    return response.data?.url || '';
  };

  const handleDownloadCsv = () => {
    if (uploadResult.items.length === 0) {
      return;
    }

    const rows = uploadResult.items.map((item) => ({
      sourceUrl: item.sourceUrl,
      targetUrl: item.targetUrl || '',
      status: getStatusText(item.status),
      errorMessage: item.errorMessage || '',
      fileName: item.fileName
    }));

    downloadToCsv(
      rows,
      {
        sourceUrl: t('urlUpload.csvSourceUrl'),
        targetUrl: t('urlUpload.csvTargetUrl'),
        status: t('urlUpload.csvStatus'),
        errorMessage: t('urlUpload.csvErrorMessage'),
        fileName: t('urlUpload.csvFileName')
      },
      buildCsvFileName()
    );
  };

  const handleUpload = async () => {
    try {
      const validUrls = validateUrls(urls);
      
      // 验证目录选择
      if (!selectedDir || selectedDir.trim() === '') {
        message.error(t('urlUpload.selectDirectoryRequired'));
        return;
      }

      if (fetchMode === 'ai' && !enableAiUrlFetch) {
        message.error(t('urlUpload.aiFetchPermissionRequired'));
        return;
      }
      
      const uploadItems: UploadProgressItem[] = validUrls.map((url) => ({
        sourceUrl: url,
        fileName: extractFileNameFromUrl(url),
        status: 'pending' as const,
        progress: 0
      }));

      setIsUploading(true);
      setUploadProgress(uploadItems);
      setUploadResult(createEmptyUploadResult());

      for (let i = 0; i < uploadItems.length; i++) {
        const currentItem = uploadItems[i];

        updateUploadItem(uploadItems, i, {
          status: 'uploading',
          progress: 0,
          message: undefined,
          errorMessage: undefined
        });
        
        try {
          const targetUrl = await uploadSingleUrl(currentItem.sourceUrl, currentItem.fileName, (progress) => {
            updateUploadItem(uploadItems, i, {
              progress,
              status: 'uploading'
            });
          });

          updateUploadItem(uploadItems, i, {
            status: 'success',
            progress: 100,
            targetUrl,
            message: t('urlUpload.uploadSuccess')
          });
        } catch (error: unknown) {
          const errorMessage = getUploadErrorMessage(error);

          addFailedFile(currentItem.fileName, errorMessage);
          updateUploadItem(uploadItems, i, {
            status: 'error',
            progress: 0,
            errorMessage,
            message: errorMessage,
            targetUrl: ''
          });
        }

        if (i < uploadItems.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 500));
        }
      }

      const completedItems = [...uploadItems];
      const successCount = completedItems.filter(item => item.status === 'success').length;
      const errorCount = completedItems.filter(item => item.status === 'error').length;
      const successUrls = completedItems
        .filter(item => item.status === 'success')
        .map(item => item.sourceUrl);

      setUploadResult({
        totalCount: completedItems.length,
        successCount,
        errorCount,
        items: completedItems,
        completedAt: new Date()
      });

      if (successCount > 0) {
        eventBus.emit('urlUploadSuccess', {
          successCount,
          successUrls,
          dir: selectedDir
        });
      }

      if (errorCount === 0) {
        message.success(t('urlUpload.allUploaded', { count: completedItems.length }));
      } else {
        message.warning(t('urlUpload.uploadSummary', { success: successCount, error: errorCount }));
      }
    } catch (error: unknown) {
      message.error(getUploadErrorMessage(error));
    } finally {
      setIsUploading(false);
    }
  };

  const getStatusColor = (status: UploadStatus) => {
    switch (status) {
      case 'pending': return '#d9d9d9';
      case 'uploading': return '#1890ff';
      case 'success': return '#52c41a';
      case 'error': return '#ff4d4f';
      default: return '#d9d9d9';
    }
  };

  const getStatusText = (status: UploadStatus) => {
    switch (status) {
      case 'pending': return t('urlUpload.statusPending');
      case 'uploading': return t('urlUpload.statusUploading');
      case 'success': return t('urlUpload.statusSuccess');
      case 'error': return t('urlUpload.statusError');
      default: return t('urlUpload.statusUnknown');
    }
  };

  return (
    <>
      <Button size="small" type="primary" onClick={showModal}>
        {t('urlUpload.title')}
      </Button>
      
      <Modal
        title={t('urlUpload.title')}
        open={isModalVisible}
        onCancel={handleCancel}
        footer={[
          <Button key="cancel" onClick={handleCancel}>
            {t('urlUpload.cancel')}
          </Button>,
          <Button 
            key="upload" 
            type="primary" 
            loading={isUploading}
            disabled={!urls.trim() || !selectedDir.trim()}
            onClick={handleUpload}
          >
            {isUploading ? t('urlUpload.uploading') : t('urlUpload.upload')}
          </Button>
        ]}
        width={800}
        destroyOnClose
      >
        <div style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Text>{t('urlUpload.selectDirectory')}:</Text>
            <TreeSelect
              value={selectedDir || undefined}
              onChange={setSelectedDir}
              style={{ width: 200 }}
              disabled={isUploading || isLoadingDirs}
              loading={isLoadingDirs}
              placeholder={isLoadingDirs ? t('common.loading') : t('urlUpload.selectDirectory')}
              size="small"
              dropdownClassName="dir-tree-switcher-right"
              treeData={dirTreeData}
              treeDefaultExpandAll
              showSearch
              treeNodeFilterProp="title"
            />
            <Button
              size="small"
              icon="🔄"
              onClick={fetchDirectoryList}
              disabled={isLoadingDirs || isUploading}
              title={t('urlUpload.refreshDirectories')}
            />
            {isLoadingDirs && (
              <Text type="secondary" style={{ fontSize: '12px' }}>
                {t('urlUpload.loadingDirectories')}
              </Text>
            )}
          </div>
        </div>

        <div style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Text>{t('urlUpload.randomPath')}:</Text>
            <Select
              value={randomPath}
              onChange={setRandomPath}
              style={{ width: 200 }}
              disabled={isUploading}
              size="small"
            >
              <Option value={false}>{t('urlUpload.randomPathNo')}</Option>
              <Option value={true}>{t('urlUpload.randomPathYes')}</Option>
            </Select>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              {t('urlUpload.randomPathHint')}
            </Text>
          </div>
        </div>

        <div style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Text>{t('urlUpload.fetchMode')}:</Text>
            <Select<FetchMode>
              value={fetchMode}
              onChange={setFetchMode}
              style={{ width: 200 }}
              disabled={isUploading || isLoadingAiPermission}
              size="small"
            >
              <Option value="fast">{t('urlUpload.fetchModeFast')}</Option>
              <Option value="ai" disabled={!enableAiUrlFetch}>
                {t('urlUpload.fetchModeAi')}
              </Option>
            </Select>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              {enableAiUrlFetch
                ? t('urlUpload.fetchModeAiHint')
                : t('urlUpload.fetchModeAiDisabledHint')}
            </Text>
          </div>
        </div>

        <div style={{ marginBottom: 16 }}>
          <Text>{t('urlUpload.urlInputHint')}</Text>
        </div>
        
        <TextArea
          rows={8}
          value={urls}
          onChange={(e) => setUrls(e.target.value)}
          placeholder={t('urlUpload.urlPlaceholder')}
          disabled={isUploading}
          onPaste={e => e.stopPropagation()}
          onKeyDown={e => e.stopPropagation()}
          style={{ marginBottom: 16 }}
        />

        {uploadProgress.length > 0 && (
          <div style={{ marginTop: 16 }}>
            <Text strong>{t('urlUpload.uploadProgress')}:</Text>
            <div style={{ marginTop: 8, maxHeight: 300, overflowY: 'auto' }}>
              {uploadProgress.map((item, index) => (
                <div key={index} style={{ marginBottom: 12 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                    <Text 
                      style={{ 
                        color: getStatusColor(item.status),
                        fontSize: '12px',
                        maxWidth: '60%',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {item.sourceUrl}
                    </Text>
                    <Text style={{ fontSize: '12px', color: getStatusColor(item.status) }}>
                      {getStatusText(item.status)}
                    </Text>
                  </div>
                  
                  {item.status === 'uploading' && (
                    <Progress 
                      percent={item.progress} 
                      size="small" 
                      strokeColor={getStatusColor(item.status)}
                    />
                  )}
                  
                  {item.status === 'success' && (
                    <Progress 
                      percent={100} 
                      size="small" 
                      strokeColor={getStatusColor(item.status)}
                      status="success"
                    />
                  )}
                  
                  {item.status === 'error' && (
                    <Progress 
                      percent={0} 
                      size="small" 
                      strokeColor={getStatusColor(item.status)}
                      status="exception"
                    />
                  )}
                  
                  {item.message && (
                    <Text 
                      style={{ 
                        fontSize: '12px', 
                        color: item.status === 'success' ? '#52c41a' : '#ff4d4f' 
                      }}
                    >
                      {item.message}
                    </Text>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 新增：上传结果展示区域 */}
        {uploadResult.completedAt && (
          <div style={{ marginTop: 16, padding: 16, backgroundColor: '#f6f8fa', borderRadius: 6 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
              <Text strong style={{ fontSize: '14px', color: '#1890ff' }}>
                {t('urlUpload.uploadSummaryTitle')}
              </Text>
              {uploadResult.items.length > 0 && (
                <Button
                  size="small"
                  icon={<DownloadOutlined />}
                  onClick={handleDownloadCsv}
                >
                  {t('urlUpload.downloadCsv')}
                </Button>
              )}
            </div>
            <div style={{ marginTop: 8 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <Text>{t('urlUpload.totalCount', { count: uploadResult.totalCount })}</Text>
                <Text style={{ color: '#52c41a' }}>{t('urlUpload.successCount', { count: uploadResult.successCount })}</Text>
                <Text style={{ color: '#ff4d4f' }}>{t('urlUpload.errorCount', { count: uploadResult.errorCount })}</Text>
              </div>
              
              {successItems.length > 0 && (
                <div style={{ marginBottom: 12 }}>
                  <Text strong style={{ color: '#52c41a' }}>{t('urlUpload.successUrls')}:</Text>
                  <div style={{ marginTop: 4, maxHeight: 120, overflowY: 'auto' }}>
                    {successItems.map((item, index) => (
                      <div key={`${item.sourceUrl}-${index}`} style={{ fontSize: '12px', color: '#52c41a', marginBottom: 8 }}>
                        <div>{t('urlUpload.csvSourceUrl')}: {item.sourceUrl}</div>
                        <div>{t('urlUpload.csvTargetUrl')}: {item.targetUrl || '-'}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {errorItems.length > 0 && (
                <div>
                  <Text strong style={{ color: '#ff4d4f' }}>{t('urlUpload.errorUrls')}:</Text>
                  <div style={{ marginTop: 4, maxHeight: 120, overflowY: 'auto' }}>
                    {errorItems.map((item, index) => (
                      <div key={`${item.sourceUrl}-${index}`} style={{ fontSize: '12px', color: '#ff4d4f', marginBottom: 8 }}>
                        <div>{t('urlUpload.csvSourceUrl')}: {item.sourceUrl}</div>
                        <div>{t('urlUpload.csvErrorMessage')}: {item.errorMessage || item.message || '-'}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              <div style={{ marginTop: 8, fontSize: '12px', color: '#8c8c8c' }}>
                {t('urlUpload.completedAt', { time: uploadResult.completedAt.toLocaleString() })}
              </div>
            </div>
          </div>
        )}
      </Modal>
    </>
  );
};

export default UrlUpload;
